import { getAnalytics, logEvent } from 'firebase/analytics';
import React from 'react';
import 'react-dropdown/style.css';
import styled from 'styled-components';
import Modal from 'react-modal';
import { saveHasSeenOnboarding } from '../../utils/localstorage';
import { device } from './../../constants/device-sizes';
import { getSiteName } from '../../hooks/useSiteConfig';

const ModalWrapper = styled.div`
  width: 95%;
  max-width: 700px;
  padding: 1rem;
  max-height: 90vh;
  overflow-y: auto;
  @media ${device.tablet} {
    width: 85%;
    max-width: 800px;
    padding: 1.5rem;
  }
`;

const ModalContent = styled.div`
  padding: 2rem;
  background: var(--clr-bg);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 8px 16px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  }

  @media ${device.tablet} {
    padding: 2.5rem;
  }
`;

const WelcomeTitle = styled.h1`
  text-align: center;
  color: var(--clr-prim);
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  @media ${device.tablet} {
    font-size: 2.5rem;
  }
`;

const SubTitle = styled.p`
  text-align: center;
  color: var(--clr-sec);
  font-size: 1rem;
  font-style: italic;
  margin-bottom: 1.5rem;
  font-weight: 400;

  @media ${device.tablet} {
    font-size: 1.2rem;
  }
`;

const FeatureSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  color: var(--clr-sec);
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '';
    width: 4px;
    height: 18px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 2px;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.li`
  color: var(--clr-sec);
  font-size: 0.9rem;
  padding-left: 1.5rem;
  position: relative;
  line-height: 1.4;

  &::before {
    content: '✨';
    position: absolute;
    left: 0;
    top: 0;
    font-size: 0.9rem;
  }

  &.rule::before {
    content: '🚫';
  }

  @media ${device.tablet} {
    font-size: 1rem;
  }
`;

const RulesSection = styled.div`
  background: rgba(231, 76, 60, 0.05);
  border-radius: 12px;
  padding: 1.25rem;
  margin: 1.5rem 0;
  border: 1px solid rgba(231, 76, 60, 0.1);
`;

const RulesText = styled.p`
  color: var(--clr-sec);
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
`;

const RulesLink = styled.a`
  color: var(--clr-purple);
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const ModernButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.875rem 2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 25px rgba(102, 126, 234, 0.4);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }

  @media ${device.tablet} {
    font-size: 1.1rem;
    padding: 1rem 2.5rem;
  }
`;

const OnboardingModal = (props) => {
  function onPressContinue() {
    const analytics = getAnalytics();
    logEvent(analytics, 'onboarding_modal_closed');
    saveHasSeenOnboarding();
    props.onClose();
  }

  return (
    <Modal
      isOpen={props.visible}
      onRequestClose={props.onClose}
      style={{
        overlay: {
          zIndex: 1000,
          backgroundColor: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(8px)',
        },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <WelcomeTitle>Welcome to {getSiteName()}</WelcomeTitle>
          <SubTitle>Chat and vent with others in real-time</SubTitle>

          <FeatureSection>
            <SectionTitle>Features</SectionTitle>
            <FeatureList>
              <FeatureItem>{getSiteName()} is an anonymous chat room</FeatureItem>
              <FeatureItem>
                All messages disappear automatically after a few minutes
              </FeatureItem>
              <FeatureItem>Everyone is anonymous</FeatureItem>
              <FeatureItem>
                Switch rooms with the door button in the top right
              </FeatureItem>
              <FeatureItem>
                Set a nickname in the settings menu (settings cog button)
              </FeatureItem>
              <FeatureItem>
                Click on a message to send a friend request or report it
              </FeatureItem>
            </FeatureList>
          </FeatureSection>

          <RulesSection>
            <SectionTitle>Rules</SectionTitle>
            <RulesText>
              Be respectful. Breaking the rules will result in a ban.{' '}
              <RulesLink href="/rules" target="_blank" rel="noopener noreferrer">
                View full rules
              </RulesLink>
            </RulesText>
          </RulesSection>

          <ButtonContainer>
            <ModernButton onClick={onPressContinue}>Let's go! 🚀</ModernButton>
          </ButtonContainer>
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default OnboardingModal;
